/**
 * Emits the analytics events for tutor-audio playback: the speed chip and
 * phrase replay.
 *
 * Shaped like useRecordingEvents.js: a frozen name map plus one thin wrapper
 * per wired event over the shared gtag transport in analyticsTransport.js, so
 * the event names and payload shapes for this feature live in one file and
 * callers cannot drift.
 *
 * All seven names are declared here. The speed chip wires SPEED_CHANGED and
 * SECONDS_LISTENED; the phrase-replay controls in TutorMessage and AudioPlayer
 * wire the other five. PLAIN_RENDER_FALLBACK counts a message that was shown
 * plain because its marked render did not match its plain render, because a
 * marker pair was lost, because the text already held a marker character, or
 * because the pass that wraps the phrases threw.
 *
 * Nothing here carries message text, transcript content, audio ids or dialog
 * ids: every wrapper builds its payload from named numeric or enumerated
 * dimensions only. `emit` never throws and silently drops events when
 * `window.gtag` is absent, so telemetry can never break playback.
 */
import { emit } from "@/composables/analyticsTransport.js";

const EVENT_NAMES = Object.freeze({
  // The learner moved the speed chip to another step.
  SPEED_CHANGED: "playback_speed_changed",
  // A phrase in the reply bubble was tapped (spoken or tail).
  PHRASE_TAPPED: "phrase_replay_tapped",
  // A tapped phrase was set to repeat.
  PHRASE_LOOPED: "phrase_replay_looped",
  // The pill's replay button replayed a span.
  SPAN_REPLAYED: "phrase_replay_span_replayed",
  // A scrub released on the waveform snapped to a span start.
  SCRUB_SNAPPED: "phrase_replay_scrub_snapped",
  // How long tutor audio played at one rate, one event per listening stretch.
  SECONDS_LISTENED: "playback_seconds_listened",
  // A message rendered plain because its phrase markers did not survive.
  PLAIN_RENDER_FALLBACK: "phrase_replay_plain_render",
});

// Why a flagged message was shown plain (src/scripts/phraseMarkers.js
// reports these). Any other value is sent as "unknown", so a typo can never
// put free text into the event.
export const PLAIN_RENDER_REASONS = Object.freeze({
  RENDER_MISMATCH: "render_mismatch",
  MARKER_IN_TEXT: "marker_in_text",
  MISSING_PAIR: "missing_pair",
  PASS_ERROR: "pass_error",
});
const KNOWN_PLAIN_RENDER_REASONS = new Set(Object.values(PLAIN_RENDER_REASONS));

// A phrase is "spoken" when its audio comes from the reply itself and "tail"
// when it comes from the silent tail (stage two).
function phraseKindOf(payload) {
  return payload?.kind === "tail" ? "tail" : "spoken";
}

// Shorter stretches are a tap that started and stopped at once, and GA4 would
// only store noise for them.
const MIN_REPORTED_SECONDS = 0.1;

/**
 * "ios" | "android" | "web". Derived here rather than at each call site so
 * every event in this family reports it the same way.
 */
function currentPlatform() {
  if (typeof window === "undefined") return "web";
  const platform = window.Capacitor?.getPlatform?.();
  return typeof platform === "string" && platform ? platform : "web";
}

function platformOf(payload) {
  return String(payload?.platform || currentPlatform()).toLowerCase();
}

export function usePlaybackEvents() {
  return {
    /**
     * The chip moved from one step to the next.
     * @param {{fromRate: number, toRate: number, platform?: string}} payload
     */
    trackSpeedChanged(payload) {
      emit(EVENT_NAMES.SPEED_CHANGED, {
        from_rate: Number(payload?.fromRate),
        to_rate: Number(payload?.toRate),
        platform: platformOf(payload),
      });
    },

    /**
     * One stretch of tutor audio that played at one rate, from its start to
     * the pause, the end, the rate change or the other clip that ended it.
     * Wall-clock seconds, which is what the lesson's time allowance counts.
     * @param {{rate: number, seconds: number, platform?: string}} payload
     */
    trackSecondsListened(payload) {
      const seconds = Number(payload?.seconds);
      if (!Number.isFinite(seconds) || seconds < MIN_REPORTED_SECONDS) return;
      emit(EVENT_NAMES.SECONDS_LISTENED, {
        rate: Number(payload?.rate),
        seconds: Math.round(seconds * 10) / 10,
        platform: platformOf(payload),
      });
    },

    /**
     * A phrase in the reply bubble was tapped and its slice asked to play.
     * @param {{kind: "spoken"|"tail", platform?: string}} payload
     */
    trackPhraseTapped(payload) {
      emit(EVENT_NAMES.PHRASE_TAPPED, {
        kind: phraseKindOf(payload),
        platform: platformOf(payload),
      });
    },

    /**
     * The repeat button beside a playing phrase turned its loop on.
     * @param {{kind: "spoken"|"tail", platform?: string}} payload
     */
    trackPhraseLooped(payload) {
      emit(EVENT_NAMES.PHRASE_LOOPED, {
        kind: phraseKindOf(payload),
        platform: platformOf(payload),
      });
    },

    /**
     * The pill's replay button restarted a span. step_back is 1 when a quick
     * second tap stepped one span further back, and 0 otherwise.
     * @param {{stepBack: boolean, platform?: string}} payload
     */
    trackSpanReplayed(payload) {
      emit(EVENT_NAMES.SPAN_REPLAYED, {
        step_back: payload?.stepBack === true ? 1 : 0,
        platform: platformOf(payload),
      });
    },

    /**
     * A scrub on a pill with span timings was released at a span start.
     * @param {{platform?: string}} [payload]
     */
    trackScrubSnapped(payload) {
      emit(EVENT_NAMES.SCRUB_SNAPPED, { platform: platformOf(payload) });
    },

    /**
     * A flagged message was shown plain instead of with its phrases.
     * @param {{reason: string, platform?: string}} payload
     */
    trackPlainRenderFallback(payload) {
      const reason = KNOWN_PLAIN_RENDER_REASONS.has(payload?.reason) ? payload.reason : "unknown";
      emit(EVENT_NAMES.PLAIN_RENDER_FALLBACK, { reason, platform: platformOf(payload) });
    },
  };
}

export { EVENT_NAMES };
